import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useRef } from "react";
import { createOrganization, getActiveProductsWithPrices, getAllOrganizationsForUser, getPendingTeamInvitationsInOrganization, getOrganizationById, getOrganizationSubscription, getTeamMembersInOrganization, updateOrganizationTitle, getUserOrganizationRole } from "@/utils/supabase-queries";
import supabaseClient from "@/utils/supabase-browser";
import { Table, UnwrapPromise } from "@/types";
import { useLoggedInUser } from "@/hooks/useLoggedInUser";
export const useUpdateOrganizationTitleMutation = t0 => {
  const $ = _c(12);
  const {
    onSuccess,
    onError,
    organizationId
  } = t0;
  const queryClient = useQueryClient();
  const toastRef = useRef(null);
  let t1;
  if ($[0] !== organizationId) {
    t1 = async t2 => {
      const {
        title
      } = t2;
      return updateOrganizationTitle(supabaseClient, organizationId, title);
    };
    $[0] = organizationId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const toastId = toast.loading("Updating organization title...");
      toastRef.current = toastId;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onSuccess || $[4] !== organizationId || $[5] !== queryClient) {
    t3 = () => {
      queryClient.invalidateQueries(["organization", organizationId]);
      toast.success("Organization title updated", {
        id: toastRef.current || ""
      });
      toastRef.current = null;
      onSuccess?.();
    };
    $[3] = onSuccess;
    $[4] = organizationId;
    $[5] = queryClient;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onError) {
    t4 = error => {
      onError?.(error);
      toast.error("Failed to update organization title", {
        id: toastRef.current || ""
      });
      toastRef.current = null;
    };
    $[7] = onError;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = {
      onMutate: t2,
      onSuccess: t3,
      onError: t4
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return useMutation(t1, t5);
};
export type InitialOrganizationListType = UnwrapPromise<ReturnType<typeof getAllOrganizationsForUser>>;
export const useOrganizationsList = initialOrganizationList => {
  const $ = _c(4);
  const user = useLoggedInUser();
  const t0 = user?.id;
  let t1;
  if ($[0] !== t0) {
    t1 = ["organization-list", t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== initialOrganizationList) {
    t2 = {
      initialData: initialOrganizationList,
      staleTime: 120000,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    };
    $[2] = initialOrganizationList;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useQuery(t1, _temp, t2);
};
export const useCreateOrganizationMutation = t0 => {
  const $ = _c(13);
  const {
    onSuccess,
    onSettled,
    onError
  } = t0;
  const user = useLoggedInUser();
  const queryClient = useQueryClient();
  const toastRef = useRef(undefined);
  let t1;
  if ($[0] !== user) {
    t1 = async name => createOrganization(supabaseClient, user, name);
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = async name_0 => {
      toastRef.current = toast.loading(`Creating organization ${name_0}...`);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onSuccess || $[4] !== queryClient || $[5] !== user?.id) {
    t3 = data => {
      queryClient.invalidateQueries(["organization-list", user?.id]);
      onSuccess?.(data);
      toast.success(`Organization ${data.title} created!`, {
        id: toastRef.current || ""
      });
      toastRef.current = undefined;
    };
    $[3] = onSuccess;
    $[4] = queryClient;
    $[5] = user?.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onError) {
    t4 = error => {
      const customError = error instanceof Error ? error : new Error(String(error));
      onError?.(customError);
      toast.error(`Error creating organization: ${customError.message}`, {
        id: toastRef.current || ""
      });
      toastRef.current = undefined;
    };
    $[7] = onError;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onSettled || $[10] !== t3 || $[11] !== t4) {
    t5 = {
      onMutate: t2,
      onSuccess: t3,
      onSettled,
      onError: t4
    };
    $[9] = onSettled;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return useMutation(t1, t5);
};
export type OrganizationByIdData = UnwrapPromise<ReturnType<typeof getOrganizationById>>;
export const useGetOrganizationById = (organizationId, initialOrganizationData) => {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] !== organizationId) {
    t0 = ["organization", organizationId];
    t1 = async () => getOrganizationById(supabaseClient, organizationId);
    $[0] = organizationId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== initialOrganizationData) {
    t2 = {
      initialData: initialOrganizationData,
      staleTime: 300000,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    };
    $[3] = initialOrganizationData;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useQuery(t0, t1, t2);
};
export const useGetTeamMembersInOrganization = organizationId => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== organizationId) {
    t0 = ["team-members", organizationId];
    t1 = async () => getTeamMembersInOrganization(supabaseClient, organizationId);
    $[0] = organizationId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  return useQuery(t0, t1);
};
export const useGetTeamInvitationsInOrganization = organizationId => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== organizationId) {
    t0 = ["team-invitations", organizationId];
    t1 = async () => getPendingTeamInvitationsInOrganization(supabaseClient, organizationId);
    $[0] = organizationId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  return useQuery(t0, t1);
};
export const useInviteUserMutation = (organizationId, t0) => {
  const $ = _c(12);
  const {
    onSuccess,
    onSettled,
    onError
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== onSuccess || $[1] !== organizationId || $[2] !== queryClient) {
    t1 = () => {
      queryClient.invalidateQueries(["team-invitations", organizationId]);
      onSuccess?.();
    };
    $[0] = onSuccess;
    $[1] = organizationId;
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== onSettled) {
    t2 = () => {
      onSettled?.();
    };
    $[4] = onSettled;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== onError) {
    t3 = error => {
      onError?.(error);
    };
    $[6] = onError;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      onSuccess: t1,
      onSettled: t2,
      onError: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useMutation(_temp2, t4);
};
export const useGetOrganizationSubscription = organizationId => {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== organizationId) {
    t0 = ["organization-subscription", organizationId];
    t1 = async () => getOrganizationSubscription(supabaseClient, organizationId);
    $[0] = organizationId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      retry: false,
      retryDelay: 0,
      refetchOnWindowFocus: false
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useQuery(t0, t1, t2);
};
export const useGetAllActiveProducts = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["all-active-products"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refetchOnWindowFocus: false
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return useQuery(t0, _temp3, t1);
};
export const useGetIsOrganizationAdmin = organizationId => {
  const $ = _c(5);
  const user = useLoggedInUser();
  let t0;
  if ($[0] !== user.id) {
    t0 = ["isOrganizationAdmin", user.id];
    $[0] = user.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== organizationId || $[3] !== user.id) {
    t1 = async () => {
      const memberInfo = await getUserOrganizationRole(supabaseClient, user.id, organizationId);
      return memberInfo.member_role === "admin" || memberInfo.member_role === "owner";
    };
    $[2] = organizationId;
    $[3] = user.id;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t0, t1);
};
async function _temp() {
  return getAllOrganizationsForUser(supabaseClient);
}
async function _temp2(t0) {
  const {
    email,
    organizationId: organizationId_0
  } = t0;
  const response = await fetch("/api/invitations/create", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify({
      email,
      organizationId: organizationId_0
    })
  });
  if (!response.ok) {
    throw new Error(response.statusText);
  }
  return await response.json();
}
async function _temp3() {
  return getActiveProductsWithPrices(supabaseClient);
}